import { parse, isValid, format } from "date-fns"

/**
 * Strapi `type: "date"` is a plain YYYY-MM-DD (no timezone).
 * Parse it as a *local calendar date* so the day is stable everywhere.
 * Never pass "YYYY-MM-DD" to `new Date(...)` directly (causes TZ drift).
 */

/** Parse a backend date string into a local Date at local midnight. */
export const parseLocalDate = raw => {
  if (!raw) return null
  if (raw instanceof Date && isValid(raw)) return raw

  if (typeof raw === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return parse(raw, "yyyy-MM-dd", new Date()) // Pure YYYY-MM-DD (Strapi `date`)
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(raw)) {
      return parse(raw, "yyyy-MM-dd'T'HH:mm:ss", new Date()) // Local timestamp without zone
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      return parse(raw, "MM/dd/yyyy", new Date()) // US display fallback
    }

    const d = new Date(raw) // as a last resort, let the browser handle the date
    return isValid(d) ? d : null
  }

  return null
}

/** Format a parsed local date; returns "" if invalid. */
export const formatLocalDate = (raw, fmt = "MMMM d, yyyy") => {
  const d = parseLocalDate(raw)
  return d && isValid(d) ? format(d, fmt) : ""
}

/**
 * Local-midnight string without timezone ("YYYY-MM-DDT00:00:00").
 * If a child does `new Date(str)`, it stays on the same calendar day.
 */
export const toLocalMidnightString = d => {
  if (!d || !isValid(d)) return ""
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}T00:00:00`
}
